import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import API from 'app/api';
import apsDataService from 'app/services/APSDataService';
import { setSnackbar } from './snackbarSlice';

const errMsg = (e) => e?.response?.data?.message ?? e?.message ?? 'Unknown error';

const APS_POPUP_FEATURES = 'width=600,height=720,menubar=no,toolbar=no,status=no';

const apiOrigin = (() => {
    try {
        return new URL(process.env.REACT_APP_API_DOMAIN).origin;
    } catch {
        return null; // relative/unset API domain (e.g. dev proxy) — skip origin check
    }
})();

// Resolves when the API callback page posts its result to us, or when the user
// closes the popup without finishing.
const waitForApsPopup = (popup) =>
    new Promise((resolve) => {
        let pollTimer = null;
        const finish = (result) => {
            window.removeEventListener('message', onMessage);
            clearInterval(pollTimer);
            resolve(result);
        };
        const onMessage = (event) => {
            if (apiOrigin && event.origin !== apiOrigin) return;
            if (event.data?.type !== 'aps-oauth-callback') return;
            if (!popup.closed) popup.close();
            finish({ completed: true, success: !!event.data.success, error: event.data.error ?? null });
        };
        window.addEventListener('message', onMessage);
        pollTimer = setInterval(() => {
            if (popup.closed) finish({ completed: false });
        }, 500);
    });

export const authorizeAps = createAsyncThunk('aps/authorize', async (_, { dispatch }) => {
    // Open the popup synchronously (still inside the click gesture) so popup
    // blockers allow it; the OAuth URL is assigned once the API responds.
    const popup = window.open('', 'aps-oauth', APS_POPUP_FEATURES);
    let data;
    try {
        ({ data } = await API.get('/aps/oauth/authorize'));
        if (!data?.success || !data?.authorizationUrl) {
            throw new Error('APS authorization URL not provided');
        }
    } catch (e) {
        popup?.close();
        dispatch(setSnackbar({ status: 'error', message: 'APS authorization failed' }));
        throw e;
    }

    if (!popup) {
        // Popup blocked — fall back to the full-page redirect flow.
        window.location.href = data.authorizationUrl;
        return data;
    }

    popup.location.href = data.authorizationUrl;
    const result = await waitForApsPopup(popup);
    if (result.completed && !result.success) {
        const message = result.error || 'Autodesk sign-in failed';
        dispatch(setSnackbar({ status: 'error', message }));
        throw new Error(message);
    }

    // Success, or the popup was closed by hand (the user may still have finished
    // the handshake) — refresh the profile to pick up the connected account.
    const user = await dispatch(fetchApsUser()).unwrap();
    if (result.completed && user) {
        dispatch(setSnackbar({ status: 'success', message: 'Signed in with Autodesk' }));
    }
    return data;
});

export const fetchApsUser = createAsyncThunk('aps/fetchUser', async (_, { dispatch, rejectWithValue }) => {
    try {
        const { data } = await API.get('/aps/userprofile');
        return data ?? null;
    } catch (e) {
        const status = e?.response?.status;
        if (status === 400 || status === 401) return null;
        dispatch(setSnackbar({ status: 'error', message: errMsg(e) }));
        return rejectWithValue(errMsg(e));
    }
});

export const logoutAps = createAsyncThunk('aps/logout', async (_, { dispatch }) => {
    await API.delete('/aps/disconnect');
    dispatch(setSnackbar({ status: 'success', message: 'Signed out of APS' }));
});

export const fetchApsHubs = createAsyncThunk('aps/fetchHubs', async (_, { dispatch }) => {
    try {
        const hubs = await apsDataService.getHubs();
        return hubs ?? [];
    } catch (e) {
        dispatch(setSnackbar({ status: 'error', message: errMsg(e) }));
        throw e;
    }
});

export const fetchApsProjects = createAsyncThunk('aps/fetchProjects', async (hubId, { dispatch }) => {
    try {
        const raw = await apsDataService.getProjects(hubId);
        const projects = (raw?.data ?? []).map((p) => ({
            id: p.id,
            name: p.attributes?.name ?? p.id,
        }));
        return { hubId, projects };
    } catch (e) {
        dispatch(setSnackbar({ status: 'error', message: errMsg(e) }));
        throw e;
    }
});

export const fetchApsProjectRootFolder = createAsyncThunk('aps/fetchRootFolder', async ({ hubId, projectId }, { dispatch }) => {
    try {
        const folder = await apsDataService.getProjectRootFolder(hubId, projectId);
        return { hubId, projectId, folder: folder ?? null };
    } catch (e) {
        dispatch(setSnackbar({ status: 'error', message: errMsg(e) }));
        throw e;
    }
});

export const fetchApsFolderContents = createAsyncThunk('aps/fetchFolderContents', async ({ projectId, folderId }, { dispatch }) => {
    try {
        const raw = await apsDataService.getFolderContents(projectId, folderId);
        const allData = raw?.data ?? [];
        const folders = allData
            .filter((i) => i.type === 'folders')
            .map((f) => ({
                id: f.id,
                name: f.attributes?.displayName ?? f.attributes?.name ?? f.id,
            }));
        const items = allData
            .filter((i) => i.type === 'items')
            .map((item) => {
                const tipId = item.relationships?.tip?.data?.id ?? item.id;
                const derivativeUrn = tipId
                    ? btoa(tipId).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '')
                    : '';
                return {
                    id: item.id,
                    name: item.attributes?.displayName ?? item.attributes?.name ?? item.id,
                    fileType: item.attributes?.extension?.type ?? item.attributes?.fileType ?? '',
                    derivativeUrn,
                };
            });
        return { projectId, folderId, folders, items };
    } catch (e) {
        dispatch(setSnackbar({ status: 'error', message: errMsg(e) }));
        throw e;
    }
});

export const fetchApsItemVersions = createAsyncThunk('aps/fetchItemVersions', async ({ projectId, itemId }, { dispatch }) => {
    try {
        const versions = await apsDataService.getItemVersions(projectId, itemId);
        return { projectId, itemId, versions: versions ?? [] };
    } catch (e) {
        dispatch(setSnackbar({ status: 'error', message: errMsg(e) }));
        throw e;
    }
});

export const fetchApsLatestItemVersion = createAsyncThunk('aps/fetchLatestItemVersion', async ({ projectId, itemId }, { dispatch }) => {
    try {
        const raw = await apsDataService.getLatestItemVersion(projectId, itemId);
        const versionData = Array.isArray(raw?.data) ? raw.data[0] : raw?.data;
        const derivativeUrn = versionData?.relationships?.derivatives?.data?.id ?? null;
        return { projectId, itemId, derivativeUrn };
    } catch (e) {
        dispatch(setSnackbar({ status: 'error', message: errMsg(e) }));
        throw e;
    }
});

export const checkApsModelViewable = createAsyncThunk('aps/checkViewable', async (urn, { dispatch }) => {
    try {
        const manifest = await apsDataService.checkViewable(urn);
        return { urn, manifest: manifest ?? null };
    } catch (e) {
        dispatch(setSnackbar({ status: 'error', message: errMsg(e) }));
        throw e;
    }
});

export const prepareApsModel = createAsyncThunk('aps/prepareModel', async (urn, { dispatch }) => {
    try {
        const result = await apsDataService.prepareModel(urn);
        dispatch(setSnackbar({ status: 'success', message: 'Model preparation requested' }));
        return result ?? null;
    } catch (e) {
        dispatch(setSnackbar({ status: 'error', message: errMsg(e) }));
        throw e;
    }
});

export const createApsTokenProvider = () => (onTokenReady) => {
    (async () => {
        try {
            const { data } = await API.get('/aps/oauth/token/viewer');
            const token = data?.token ?? '';
            const ttl = Number.isFinite(data?.expiresIn) ? data.expiresIn : 0;
            onTokenReady(token, Math.max(0, ttl));
        } catch (e) {
            console.error('APS viewer token error:', e);
            onTokenReady('', 0);
        }
    })();
};

const apsSlice = createSlice({
    name: 'aps',
    initialState: {
        loading: false,
        error: null,
        user: null,
        hubs: [],
        projectsByHub: {},
        rootFolderByProject: {},
        folderContentsByProject: {},
        itemVersionsByItem: {},
        latestItemVersionByItem: {},
        manifests: {},
    },
    reducers: {
        clear: () => apsSlice.getInitialState(),
    },
    extraReducers: (builder) => {
        builder
            .addCase(authorizeAps.pending, (state) => { state.loading = true; state.error = null; })
            .addCase(authorizeAps.fulfilled, (state) => { state.loading = false; })
            .addCase(authorizeAps.rejected, (state, action) => { state.loading = false; state.error = action.error.message; })
            .addCase(fetchApsUser.pending, (state) => { state.loading = true; state.error = null; })
            .addCase(fetchApsUser.fulfilled, (state, action) => { state.loading = false; state.user = action.payload; })
            .addCase(fetchApsUser.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
            .addCase(logoutAps.pending, (state) => { state.loading = true; })
            .addCase(logoutAps.fulfilled, () => apsSlice.getInitialState())
            .addCase(logoutAps.rejected, (state, action) => { state.loading = false; state.error = action.error.message; })
            .addCase(fetchApsHubs.pending, (state) => { state.loading = true; state.error = null; })
            .addCase(fetchApsHubs.fulfilled, (state, action) => { state.loading = false; state.hubs = action.payload; })
            .addCase(fetchApsHubs.rejected, (state, action) => { state.loading = false; state.error = action.error.message; })
            .addCase(fetchApsProjects.pending, (state) => { state.loading = true; state.error = null; })
            .addCase(fetchApsProjects.fulfilled, (state, action) => {
                state.loading = false;
                state.projectsByHub[action.payload.hubId] = action.payload.projects;
            })
            .addCase(fetchApsProjects.rejected, (state, action) => { state.loading = false; state.error = action.error.message; })
            .addCase(fetchApsProjectRootFolder.pending, (state) => { state.loading = true; state.error = null; })
            .addCase(fetchApsProjectRootFolder.fulfilled, (state, action) => {
                state.loading = false;
                const key = `${action.payload.hubId}:${action.payload.projectId}`;
                state.rootFolderByProject[key] = action.payload.folder;
            })
            .addCase(fetchApsProjectRootFolder.rejected, (state, action) => { state.loading = false; state.error = action.error.message; })
            .addCase(fetchApsFolderContents.pending, (state) => { state.loading = true; state.error = null; })
            .addCase(fetchApsFolderContents.fulfilled, (state, action) => {
                state.loading = false;
                const key = `${action.payload.projectId}:${action.payload.folderId}`;
                state.folderContentsByProject[key] = { folders: action.payload.folders, items: action.payload.items };
            })
            .addCase(fetchApsFolderContents.rejected, (state, action) => { state.loading = false; state.error = action.error.message; })
            .addCase(fetchApsItemVersions.pending, (state) => { state.loading = true; state.error = null; })
            .addCase(fetchApsItemVersions.fulfilled, (state, action) => {
                state.loading = false;
                const key = `${action.payload.projectId}:${action.payload.itemId}`;
                state.itemVersionsByItem[key] = action.payload.versions;
            })
            .addCase(fetchApsItemVersions.rejected, (state, action) => { state.loading = false; state.error = action.error.message; })
            .addCase(fetchApsLatestItemVersion.pending, (state) => { state.loading = true; state.error = null; })
            .addCase(fetchApsLatestItemVersion.fulfilled, (state, action) => {
                state.loading = false;
                const key = `${action.payload.projectId}:${action.payload.itemId}`;
                state.latestItemVersionByItem[key] = action.payload.derivativeUrn;
            })
            .addCase(fetchApsLatestItemVersion.rejected, (state, action) => { state.loading = false; state.error = action.error.message; })
            .addCase(checkApsModelViewable.pending, (state) => { state.loading = true; state.error = null; })
            .addCase(checkApsModelViewable.fulfilled, (state, action) => {
                state.loading = false;
                state.manifests[action.payload.urn] = action.payload.manifest;
            })
            .addCase(checkApsModelViewable.rejected, (state, action) => { state.loading = false; state.error = action.error.message; })
            .addCase(prepareApsModel.pending, (state) => { state.loading = true; state.error = null; })
            .addCase(prepareApsModel.fulfilled, (state) => { state.loading = false; })
            .addCase(prepareApsModel.rejected, (state, action) => { state.loading = false; state.error = action.error.message; });
    },
});

export const { clear } = apsSlice.actions;
export default apsSlice.reducer;
